// type-specific renderer for SNMP evidence. renders the audit summary,
// system info, interfaces, routes, ARP/neighbours and open-ports cards
// when the corresponding raw_json fields exist. raw JSON is left to
// the parent drawer so the same "collapsed + copy" behaviour applies
// regardless of evidence type

import React from 'react'
import { AlertTriangle } from 'lucide-react'
import type { EngagementEvidence } from '@/lib/engagementsApi'
import {
  displayCommunity,
  extractArp,
  extractAuditSummary,
  extractInterfaces,
  extractNeighbors,
  extractOpenPorts,
  extractRoutes,
  extractSystemInfo,
  humanEvidenceType,
  snmpCommunityRisk,
  SNMP_RECOMMENDATION,
  type SnmpAuditSummary,
} from './snmpEvidence'

interface Props {
  evidence: EngagementEvidence
}

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[10px] uppercase tracking-wide text-slate-400">{label}</span>
    <span className="text-xs text-slate-700 dark:text-slate-200 break-words">{value}</span>
  </div>
)

const Card: React.FC<{ title: string; children: React.ReactNode; show?: boolean }> = ({ title, children, show = true }) => {
  if (!show) return null
  return (
    <section className="border-t border-slate-100 first:border-t-0 px-4 py-3">
      <h3 className="text-[10px] uppercase tracking-wide text-slate-400 mb-2 font-semibold">{title}</h3>
      {children}
    </section>
  )
}

const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-2 gap-x-3 gap-y-2">{children}</div>
)

export const SnmpEvidenceCards: React.FC<Props> = ({ evidence }) => {
  // raw_json may arrive as a double-encoded string. extractors call
  // normalizeRawJson internally so we pass the original value through
  const raw   = evidence.raw_json
  const type  = evidence.evidence_type
  const audit = extractAuditSummary(raw)
  const sys   = extractSystemInfo(raw)
  const ifs   = extractInterfaces(raw, type)
  const rts   = extractRoutes(raw, type)
  const arp   = extractArp(raw, type)
  const nbr   = extractNeighbors(raw, type)
  const ports = extractOpenPorts(raw)
  const community = displayCommunity(audit)
  const showCommunityCard =
    !!community ||
    evidence.evidence_type === 'snmp_default_community' ||
    evidence.evidence_type === 'snmp_sensitive_community'

  // audit summary is informative as long as we have ANY field. empty
  // audits hide the card to avoid a row of em-dashes
  const hasAuditFields = Object.values(audit).some(v => v !== null && v !== undefined &&
    !(Array.isArray(v) && v.length === 0))

  return (
    <>
      {hasAuditFields && (
        <Card title="SNMP audit summary">
          <Grid>
            <Field label="Type"   value={humanEvidenceType(evidence.evidence_type)} />
            <Field label="Target" value={
              <span className="font-mono">
                {audit.target_ip
                  ? `${audit.target_ip}${audit.target_port ? `:${audit.target_port}` : ''}`
                  : (audit.target ?? '—')}
              </span>
            } />
            {community && (
              <Field label="Community" value={<span className="font-mono">{community}</span>} />
            )}
            {audit.version           && <Field label="Version"      value={audit.version} />}
            {audit.audit_mode        && <Field label="Audit mode"   value={audit.audit_mode} />}
            {audit.dictionary_source && <Field label="Dictionary source" value={audit.dictionary_source} />}
            {audit.dictionary_size != null && <Field label="Dictionary size" value={String(audit.dictionary_size)} />}
            {audit.tested_communities != null && (
              <Field label="Tested communities" value={
                typeof audit.tested_communities === 'number'
                  ? String(audit.tested_communities)
                  : `${audit.tested_communities.length} tested`
              } />
            )}
            {Array.isArray(audit.valid_communities) && audit.valid_communities.length > 0 && (
              <div className="col-span-2">
                <Field label="Valid communities" value={
                  <span className="flex flex-wrap gap-1">
                    {audit.valid_communities.map(c => (
                      <CommunityChip key={c} community={c} highlight={c === audit.enumeration_community} />
                    ))}
                  </span>
                } />
              </div>
            )}
            {audit.enumeration_community && <Field label="Enumeration used" value={
              <CommunityChip community={audit.enumeration_community} highlight />
            } />}
            {audit.enumeration_reason && (
              <div className="col-span-2">
                <Field label="Enumeration reason" value={audit.enumeration_reason} />
              </div>
            )}
            {audit.access_classification && (
              <div className="col-span-2">
                <Field label="Access" value={<span className="text-[11px]">{audit.access_classification}</span>} />
              </div>
            )}
            {audit.no_destructive_probe === true && (
              <div className="col-span-2 text-[11px] text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
                ✓ No destructive write probe performed
              </div>
            )}
          </Grid>
        </Card>
      )}

      {showCommunityCard && (
        <Card title="Community classification">
          <CommunityCard audit={audit} />
        </Card>
      )}

      {hasSystemInfo(sys) && (
        <Card title="System information">
          <Grid>
            {sys.sysName && <Field label="sysName" value={sys.sysName} />}
            {sys.uptime != null && <Field label="Uptime" value={String(sys.uptime)} />}
            {sys.sysObjectID && (
              <div className="col-span-2"><Field label="sysObjectID" value={<span className="font-mono text-[11px]">{sys.sysObjectID}</span>} /></div>
            )}
            {sys.sysDescr && (
              <div className="col-span-2"><Field label="sysDescr" value={<span className="text-[11px]">{sys.sysDescr}</span>} /></div>
            )}
            {sys.sysLocation && <Field label="sysLocation" value={sys.sysLocation} />}
            {sys.sysContact  && <Field label="sysContact"  value={sys.sysContact} />}
            {sys.vendor      && <Field label="Vendor"      value={sys.vendor} />}
            {sys.model       && <Field label="Model"       value={sys.model} />}
            {sys.deviceType  && <Field label="Device type" value={sys.deviceType} />}
          </Grid>
        </Card>
      )}

      {ifs.length > 0 && (
        <Card title={`Interfaces (${ifs.length})`}>
          <div className="max-h-72 overflow-auto -mx-4">
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left text-slate-500">
                  <th className="px-2 py-1 font-medium">ifIndex</th>
                  <th className="px-2 py-1 font-medium">Name / descr</th>
                  <th className="px-2 py-1 font-medium">MAC</th>
                  <th className="px-2 py-1 font-medium">Speed</th>
                  <th className="px-2 py-1 font-medium">Admin / Oper</th>
                  <th className="px-2 py-1 font-medium">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {ifs.map((r, i) => (
                  <tr key={String(r.index ?? i)} className="hover:bg-slate-50">
                    <td className="px-2 py-1 tabular-nums">{r.index ?? '—'}</td>
                    <td className="px-2 py-1">
                      <div className="font-medium">{r.name ?? '—'}</div>
                      {r.description && <div className="text-slate-400 text-[10px]">{r.description}</div>}
                    </td>
                    <td className="px-2 py-1 font-mono">{r.mac ?? '—'}</td>
                    <td className="px-2 py-1 text-slate-500">{r.speed ?? '—'}</td>
                    <td className="px-2 py-1 text-slate-500">{(r.admin_status ?? '—') + ' / ' + (r.oper_status ?? '—')}</td>
                    <td className="px-2 py-1 font-mono">{r.ip ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {rts.length > 0 && (
        <Card title={`Routes (${rts.length})`}>
          <div className="max-h-60 overflow-auto -mx-4">
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left text-slate-500">
                  <th className="px-2 py-1 font-medium">Destination</th>
                  <th className="px-2 py-1 font-medium">Prefix / mask</th>
                  <th className="px-2 py-1 font-medium">Next hop</th>
                  <th className="px-2 py-1 font-medium">Interface</th>
                  <th className="px-2 py-1 font-medium">Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rts.map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-2 py-1 font-mono">{r.destination ?? '—'}</td>
                    <td className="px-2 py-1 font-mono">{r.prefix ?? '—'}</td>
                    <td className="px-2 py-1 font-mono">{r.next_hop ?? '—'}</td>
                    <td className="px-2 py-1">{r.interface ?? '—'}</td>
                    <td className="px-2 py-1 text-slate-500">{r.type ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {(arp.length > 0 || nbr.length > 0) && (
        <Card title={`ARP / neighbours (${arp.length + nbr.length})`}>
          <div className="max-h-60 overflow-auto -mx-4">
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left text-slate-500">
                  <th className="px-2 py-1 font-medium">Source</th>
                  <th className="px-2 py-1 font-medium">IP</th>
                  <th className="px-2 py-1 font-medium">MAC</th>
                  <th className="px-2 py-1 font-medium">Interface</th>
                  <th className="px-2 py-1 font-medium">Type / state</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {arp.map((r, i) => (
                  <tr key={`arp:${i}`} className="hover:bg-slate-50">
                    <td className="px-2 py-1 text-[10px] uppercase text-slate-400">ARP</td>
                    <td className="px-2 py-1 font-mono">{r.ip ?? '—'}</td>
                    <td className="px-2 py-1 font-mono">{r.mac ?? '—'}</td>
                    <td className="px-2 py-1">{r.interface ?? '—'}</td>
                    <td className="px-2 py-1 text-slate-500">{r.type ?? '—'}</td>
                  </tr>
                ))}
                {nbr.map((r, i) => (
                  <tr key={`nbr:${i}`} className="hover:bg-slate-50">
                    <td className="px-2 py-1 text-[10px] uppercase text-slate-400">Neighbour</td>
                    <td className="px-2 py-1 font-mono">{r.ip ?? '—'}</td>
                    <td className="px-2 py-1 font-mono">{r.mac ?? '—'}</td>
                    <td className="px-2 py-1">{r.interface ?? '—'}</td>
                    <td className="px-2 py-1 text-slate-500">{r.type ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {ports.length > 0 && (
        <Card title={`Open ports (${ports.length})`}>
          <div className="flex flex-wrap gap-1.5">
            {ports.map((p, i) => (
              <span key={i} className="font-mono text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-white text-slate-700"
                    title={p.banner ?? ''}>
                {p.port}/{p.protocol}
                {p.service && <span className="text-slate-400 ml-1">{p.service}</span>}
              </span>
            ))}
          </div>
        </Card>
      )}

      <Card title="Recommendation">
        <p className="text-xs text-slate-700 dark:text-slate-200 leading-relaxed">{SNMP_RECOMMENDATION}</p>
      </Card>
    </>
  )
}

function hasSystemInfo(sys: ReturnType<typeof extractSystemInfo>): boolean {
  return !!(sys.sysName || sys.sysDescr || sys.sysObjectID || sys.sysLocation || sys.sysContact ||
    sys.uptime || sys.vendor || sys.model || sys.deviceType)
}

const CommunityCard: React.FC<{ audit: SnmpAuditSummary }> = ({ audit }) => {
  const community = displayCommunity(audit)
  const risk = snmpCommunityRisk(community)
  const tone = risk === 'critical'
    ? { border: 'border-rose-400', text: 'text-rose-700', pill: 'bg-rose-100 text-rose-700' }
    : risk === 'high'
    ? { border: 'border-orange-400', text: 'text-orange-700', pill: 'bg-orange-100 text-orange-700' }
    : { border: 'border-amber-400', text: 'text-amber-700', pill: 'bg-amber-100 text-amber-700' }
  const title = community
    ? (risk === 'critical'
        ? `SNMP sensitive community detected: ${community}`
        : risk === 'high'
        ? `SNMP default community detected: ${community}`
        : `SNMP community detected: ${community}`)
    : 'SNMP community accepted'
  // body text, never produces the broken "community community granted"
  // wording. when we have a community value we name it, otherwise fall
  // back to a generic acceptance line
  const body = community
    ? (risk === 'critical'
        ? <>Read access to SNMP via the sensitive community <span className="font-mono">{community}</span> was granted. SNMP-readable management data was retrieved.</>
        : risk === 'high'
        ? <>Read access to SNMP via the default community <span className="font-mono">{community}</span> was granted. SNMP-readable management data was retrieved.</>
        : <>Read access to SNMP via community <span className="font-mono">{community}</span> was granted.</>)
    : <>An SNMP community was accepted on this host. The exact community string could not be extracted from the evidence record; check Raw JSON.</>
  return (
    <div className={`bg-white dark:bg-slate-800 border-l-4 ${tone.border} rounded p-3`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className={`h-4 w-4 mt-0.5 ${tone.text}`} />
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-semibold ${tone.text}`}>{title}</div>
          {community && (
            <div className="mt-1 flex flex-wrap gap-1.5 items-center">
              <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{community}</span>
              <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${tone.pill}`}>
                {risk}
              </span>
              {audit.community_role && (
                <span className="text-[10px] text-slate-500">{audit.community_role}</span>
              )}
            </div>
          )}
          <p className="text-[11px] text-slate-600 dark:text-slate-300 mt-2 leading-relaxed">{body}</p>
        </div>
      </div>
    </div>
  )
}

const CommunityChip: React.FC<{ community: string; highlight?: boolean }> = ({ community, highlight }) => {
  const risk = snmpCommunityRisk(community)
  const base = risk === 'critical' ? 'bg-rose-100 text-rose-700'
             : risk === 'high'     ? 'bg-orange-100 text-orange-700'
             : 'bg-slate-100 text-slate-600'
  const ring = highlight ? 'ring-2 ring-blue-400 ring-offset-1' : ''
  return (
    <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${base} ${ring}`} title={highlight ? 'Used for enumeration' : undefined}>
      {community}
    </span>
  )
}
